import api from "@/modules/api";
import { useMate } from "@/stores/mate";
import {
  HouseMap,
  MateHouseSessionEvent,
  MateHouseSessionMessage,
} from "@/types/entity";
import { defineStore } from "pinia";
import TiledMap from "tiled-types";
import { useHouseSession } from "./houseSession";
import { fixNumber } from "@/components/Mission/MissionStat";
import router from "@/router";
import { useGlobalUser } from "@/stores/globalUser";
import { showConfirmDialog } from "vant";
import i18n from "@/locales";
import { MAP_CONFIGS, MapType } from "../utils/useMap";
import { decodeData } from "../utils/useMyHouseMap";
import { MyHouseData } from "../types/furnish";

export const IDEA_INTERVAL_SEC = 40;

/**
 * Vue和Phaser需要共同管理 || 希望保持响应性的数据放在这里
 * 注意：响应性数据如果作为`Game.ts`的成员变量，会丢失。因此请放在store中
 */
export const useHouseScene = defineStore("mateHouseScene", {
  state: () => ({
    initialZoom: 1,
    followingCharId: null as string | null,
    interval: null as number | null,
    currentMapType: MapType.MY_HOUSE,
    mapJson: null as TiledMap | MyHouseData | null,
    showCharacterDetail: false,
    ui: {
      kickoffChoosenMateId: null as number | null,
    },
    radio: {
      prevIsPlaying: false,
      isPlaying: false,
      isLoading: false,
      ref: null as HTMLAudioElement | null,
    },
    currentEvent: null as MateHouseSessionEvent | null,
  }),
  getters: {
    minZoom: (state) => state.initialZoom / 2,
    maxZoom: (state) => state.initialZoom * 2,
    validSprites: () => {
      const houseSessionStore = useHouseSession();
      return (
        houseSessionStore.houseSession?.mateSprites.filter(
          (item) => item.animationzz?.length ?? 0 > 0
        ) ?? []
      );
    },
  },
  actions: {
    startIntervalThinking(immediate = true) {
      if (immediate) {
        this.requestThought();
      }
      this.interval = setInterval(() => {
        this.requestThought();
        this.refreshSubject();
      }, IDEA_INTERVAL_SEC * 1000) as unknown as number;
    },
    async requestThought() {
      const houseSessionStore = useHouseSession();
      await houseSessionStore.fetchNewerFullList({ isGenerateIdeas: 1 });
      if (houseSessionStore.subjectUpdateTime) {
        // 检查session.dtUpdate > newMessage.dtCreate
        const newMessages: MateHouseSessionMessage[] = Object.values(
          houseSessionStore.mateId2HeadMsg
        ).filter((item) => !!item);
        const invalidMsgIds = newMessages.reduce((prev, item) => {
          if (
            new Date(item.dtCreate).getTime() >
            houseSessionStore.subjectUpdateTime!
          ) {
            prev.push(item.id!);
          }
          return prev;
        }, [] as number[]);
        if (invalidMsgIds.length === 0) {
          houseSessionStore.subjectUpdateTime = null;
          return;
        }
        houseSessionStore.fullMessageListState.list =
          houseSessionStore.fullMessageListState.list.filter(
            (item) => !invalidMsgIds.includes(item as number)
          );

        const promises = invalidMsgIds.map((id) =>
          api.deleteMateHouseSessionMessage(
            houseSessionStore.houseSession!.id,
            id
          )
        );
        await Promise.all(promises);
        houseSessionStore.subjectUpdateTime = null;
      }
    },
    async refreshSubject() {
      const houseSessionStore = useHouseSession();
      const houseSession = houseSessionStore.houseSession;
      if (
        !houseSession ||
        !houseSession.isAutoUpdateTopic ||
        !houseSessionStore.isThoughtActivated
      ) {
        return;
      }
      await houseSessionStore.getOrFetchHouseSession(undefined, true);
    },
    stopIntervalThinking() {
      if (this.interval) {
        clearInterval(this.interval);
        this.interval = null;
      }
    },
    clearState() {
      this.stopIntervalThinking();
    },
    async loadRoomJson() {
      const userStore = useGlobalUser();
      const defaultHouseMap: HouseMap | null = userStore.user.defaultHouseMap;
      this.currentMapType = defaultHouseMap ? MapType.MY_HOUSE : MapType.ROOM;
      const mapConfig = MAP_CONFIGS[this.currentMapType];
      if (defaultHouseMap?.media?.url) {
        const encodedStr = await fetch(defaultHouseMap.media.url).then((res) =>
          res.text()
        );
        const decodedStr = JSON.parse(decodeData(encodedStr)!);
        this.mapJson = decodedStr as MyHouseData;
      } else {
        const roomJson = await fetch(mapConfig.path!).then((res) => res.json());
        this.mapJson = roomJson;
      }
    },
    async adaptCharacter(presetMateId: number, isKickoff = false) {
      // 1. 判断当前角色是否已经被clone过了
      const mateStore = useMate();
      // const hasCloned = mateStore.fullList
      //   ?.filter((item) => item.mateLink)
      //   .find((item) => item.mateLink?.sourceMateId === presetMateId);
      // if (hasCloned) {
      //   return;
      // }
      // 2. mateClone
      const [{ item: targetMate }] = await Promise.all([
        api.cloneOneMate(presetMateId, true),
        mateStore.updateMateDetail(mateStore.userMate.uuid, {
          isHouseEnabled: true,
        }),
      ]);
      if (isKickoff) {
        this.ui.kickoffChoosenMateId = targetMate.id;
      }
      // 3. 刷新mate列表
      await mateStore.fetchMateList();
      // 4. 将角色加入到小屋中, ADD接口中会刷新store中的houseSession
      const houseSessionStore = useHouseSession();
      const spriteId = mateStore
        .getMyMateById(targetMate.id)
        ?.spritezz?.find((item) => item.imageMedia)?.id;
      if (spriteId) {
        await houseSessionStore.addSpriteToHouseSession(spriteId);
      } else {
        console.error("cloneOneMate failed, no sprite found");
      }
      // 5. 更新subject
      if (isKickoff) {
        const userMate = this.validSprites.find(
          (item) => item.mateId !== targetMate.id
        );
        if (userMate) {
          await houseSessionStore.updateHouseSessionSubject(
            `${userMate?.mate?.name}搬进了新家，对这个社区充满了期待`
          );
        }
      }
      // 6. 请求一次想法
      await houseSessionStore.fetchNewerFullList({ isGenerateIdeas: 1 });
    },
    async startEvent(eventId: number) {
      const houseSessionStore = useHouseSession();
      const item = await houseSessionStore.fetchSessionEvent(eventId);
      this.currentEvent = item;
    },
    pauseRadio() {
      this.radio.prevIsPlaying = this.radio.isPlaying;
      this.radio.ref?.pause();
    },
    resumeRadio() {
      if (this.radio.prevIsPlaying) {
        this.radio.ref?.play();
      }
    },
    estimateThoughtCostByMateCounte(mateCount: number) {
      const houseStore = useHouseSession();
      return fixNumber(
        mateCount *
          Math.floor(60 / IDEA_INTERVAL_SEC) *
          (1 + houseStore.extraPower)
      );
    },
    /**
     *
     * @returns 是否可以继续
     */
    async checkPowerAmountForThought(): Promise<boolean> {
      const userStore = useGlobalUser();
      await userStore.refreshUser();
      const cost = this.estimateThoughtCostByMateCounte(1);
      if (cost > userStore.user.powerAmount) {
        try {
          await showConfirmDialog({
            title: i18n.global.t("house.insufficientPower.title"),
            message: i18n.global.t("house.insufficientPower.message"),
            confirmButtonText: i18n.global.t(
              "house.insufficientPower.confirmButtonText"
            ),
            cancelButtonText: i18n.global.t(
              "house.insufficientPower.cancelButtonText"
            ),
          });
          await router.push({ name: "Crystal", hash: "#2" });
          return false;
        } catch {
          // ignore
          return true;
        }
      }
      return true;
    },
  },
});
